import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authService } from '../services/auth';
import { Mail, ArrowLeft, Shield, Lock, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const handleBlur = () => {
    setTouched(true);
    if (!email) {
      setError('El correo es requerido');
    } else if (!validateEmail(email)) {
      setError('Ingresa un correo válido');
    } else {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !validateEmail(email)) {
      setTouched(true);
      setError('Ingresa un correo válido');
      return;
    }

    setLoading(true);

    try {
      await authService.requestPasswordReset(email);
      setSubmitted(true);
      toast.success('Solicitud enviada correctamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Ocurrió un error. Inténtalo nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex bg-gray-50">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white">
          <div className="mx-auto w-full max-w-sm text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¡Revisa tu correo!
            </h2>
            
            <p className="text-gray-600 mb-8 leading-relaxed">
              Si el correo <span className="font-semibold text-gray-800">{email}</span> existe en nuestra base de datos, recibirás un enlace de recuperación en unos momentos.
            </p>

            <div className="space-y-4">
              <Link
                to="/app/login"
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30"
              >
                Volver al inicio de sesión
              </Link>
              
              <button 
                onClick={() => setSubmitted(false)}
                className="text-sm text-gray-500 hover:text-[#2A9D8F] transition-colors"
              >
                Intentar con otro correo
              </button>
            </div>
          </div>
        </div>
        
        {/* Panel Derecho Decorativo (Mismo estilo que Login) */}
        <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
          </div>
          <div className="relative flex flex-col justify-center px-16 xl:px-20 text-center">
             <Shield className="w-24 h-24 text-[#2A9D8F] mx-auto mb-6 opacity-80" />
             <h3 className="text-2xl font-bold text-gray-800 mb-2">Seguridad Garantizada</h3>
             <p className="text-gray-600">Protegemos tus datos con los más altos estándares.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* ============================================= */}
      {/* SECCIÓN IZQUIERDA - FORMULARIO */}
      {/* ============================================= */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-md">
          {/* Logo y Header */}
          <div className="text-center mb-10">
            <img
              src="/logo-symbol.png"
              alt="EstetikFlow"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-[#1F7A6F] tracking-tight">
              Recuperar Contraseña
            </h1>
            <p className="mt-3 text-gray-500 text-base">
              Ingresa tu correo y te enviaremos las instrucciones.
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Correo Electrónico
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched && error 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="ejemplo@correo.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (touched) setError('');
                  }}
                  onBlur={handleBlur}
                />
              </div>
              {touched && error && (
                <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                  <span>•</span> {error}
                </p>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30 hover:shadow-xl hover:shadow-[#E76F51]/40 transform hover:-translate-y-0.5 active:translate-y-0"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando...
                  </>
                ) : (
                  'Enviar enlace de recuperación'
                )}
              </button>
            </div>

            <div className="text-center mt-6">
              <Link
                to="/app/login"
                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-[#2A9D8F] transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al inicio de sesión
              </Link>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-400">
              © 2025 EstetikFlow. Gestión inteligente.
            </p>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* SECCIÓN DERECHA - VISUAL */}
      {/* ============================================= */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
        {/* Patrón decorativo */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
        </div>

        {/* Contenido */}
        <div className="relative flex flex-col justify-center px-16 xl:px-20">
          <div className="max-w-md mx-auto text-center space-y-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/60 backdrop-blur-md rounded-2xl shadow-sm mb-4">
              <Lock className="w-10 h-10 text-[#2A9D8F]" />
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                ¿Problemas para entrar?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No te preocupes. Es común olvidar contraseñas. Te ayudaremos a recuperar el acceso a tu cuenta de forma segura y rápida.
              </p>
            </div>

            <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/60 text-left">
              <div className="flex items-start gap-4">
                <Shield className="w-6 h-6 text-[#E76F51] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">Seguridad Garantizada</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Nunca compartas tu contraseña con nadie. Nuestro equipo nunca te pedirá tu clave por correo o teléfono.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
